import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast, toastVariant } from 'components/ToastProvider';
import ActionModal from '../ActionModal';
import { Wrapper } from '../../styles';
import { TOAST_DELAY } from './constants';
import {
  CUSTOM_ACTION_IDENTIFIER,
  useGetSummary,
} from 'pages/Common/bulkactions/model';
import PerformAction from 'services/PerformActionService';
import SummaryStep from 'pages/Common/bulkactions/SummaryStep';
import Loader from 'components/Kizen/Loader';

export const CustomAction = ({
  access,
  entityId,
  fullName,
  onHide,
  isMobile,
  isClient,
  customAction,
  ...props
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [performingAction, setPerformingAction] = useState(false);

  const { selection, selectedAction, objectInfo } = useMemo(
    () => ({
      selection: { value: 'individual', checkedIds: [entityId] },
      selectedAction: { value: CUSTOM_ACTION_IDENTIFIER },
      objectInfo: {
        names: isClient
          ? {
              singular: t('Contact'),
              plural: t('Contacts'),
            }
          : {
              singular: t('Record'),
              plural: t('Records'),
            },
      },
    }),
    [entityId, isClient, t]
  );

  const [{ summary, isFetching, isPolling }, getSummaryFn] = useGetSummary({
    selection,
    selectedAction,
    customAction,
  });

  const isLoadingSummary = isFetching || isPolling;
  const isFailed = !isLoadingSummary && !summary?.cache_key;

  useEffect(() => {
    getSummaryFn();
  }, [getSummaryFn]);

  const handleCancel = useCallback(() => {
    onHide();
  }, [onHide]);

  const handleSubmit = async () => {
    try {
      setPerformingAction(true);
      await PerformAction.performCustomAction({
        entity_records_set_key: summary?.cache_key,
        custom_action_id: customAction.id,
      });

      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          '{{noun}} has been successfully queued on {{name}}. You will receive an email once this action is complete.',
          {
            name: fullName,
            noun: customAction.name,
          }
        ),
        delay: TOAST_DELAY,
      });
    } catch (error) {
      setPerformingAction(false);
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The {{noun}} was not successfully queued. Please try again or contact Kizen support.',
          {
            noun: customAction.name,
          }
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  return (
    <ActionModal
      disableButton={performingAction || isFetching || isPolling}
      onHide={handleCancel}
      title={t('{{name}} on {{fullName}}', {
        fullName,
        name: customAction.name,
      })}
      show
      onSubmit={handleSubmit}
      isMobile={isMobile}
      isFailed={isFailed}
    >
      <Wrapper {...props}>
        <Loader loading={isFetching || isPolling}>
          <SummaryStep
            userAction={selectedAction}
            summary={summary?.stats && { ...summary.stats, text: fullName }}
            isLoadingSummary={isFetching || isPolling}
            customAction={customAction}
            objectInfo={objectInfo}
          />
        </Loader>
      </Wrapper>
    </ActionModal>
  );
};
